import ServiceLayout from "@/components/ServiceLayout";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

export default function Pflasterarbeiten() {
  return (
    <ServiceLayout
      title="Pflasterarbeiten in Augsburg"
      description="Saubere Pflasterarbeiten und präzise Plattenverlegung für Einfahrt, Terrasse und Hof – langlebig umgesetzt, fachgerecht vorbereitet und optisch stimmig."
      image="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6946e67759a0a60a8a0e474a.jpg"
      content={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Robuste und schöne Außenanlagen
          </h2>
          <p className="text-lg text-neutral-600">
            Eine professionell gepflasterte Einfahrt oder Terrasse wertet jede Immobilie optisch auf und bietet langlebigen Komfort. Wir sind Ihr zuverlässiger Partner für Pflasterarbeiten aller Art in Augsburg und Umgebung.
          </p>
          <p className="text-lg text-neutral-600">
            Von der gründlichen Vorbereitung des Untergrunds über die fachgerechte Verlegung bis hin zur sauberen Verfugung – wir garantieren Ihnen ein Ergebnis, das nicht nur gut aussieht, sondern auch höchsten Belastungen standhält.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Pflastern von Einfahrten und Höfen",
              "Verlegung von Terrassenplatten",
              "Fachgerechter Unterbau",
              "Langlebige und stabile Ergebnisse"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-neutral-800 font-medium">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </>
      }
      extendedContent={
        <div className="bg-white">
          <section className="py-16 bg-neutral-50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Professionelle Pflasterarbeiten in Augsburg – Das Fundament für Ihren Außenbereich</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Der erste Eindruck zählt – und das gilt besonders für Ihr Grundstück. Eine sauber gepflasterte Einfahrt, ein einladender Weg zur Haustür oder eine stilvoll gestaltete Terrasse sind die Visitenkarte Ihres Hauses. Als erfahrener Fachbetrieb für Pflasterarbeiten in Augsburg verwandeln wir unebene Flächen und alte Beläge in repräsentative und langlebige Außenanlagen. Wir verbinden handwerkliche Präzision mit hochwertigen Materialien, um Ergebnisse zu schaffen, an denen Sie jahrzehntelang Freude haben.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Pflasterarbeiten erfordern weit mehr als nur das Aneinanderlegen von Steinen. Die größte Herausforderung liegt im Unsichtbaren: dem Unterbau. Ein falsch dimensionierter oder schlecht verdichteter Untergrund führt unweigerlich zu Absackungen, Pfützenbildung und Frostschäden. Mit unserem fundierten Fachwissen sorgen wir für einen normgerechten Schichtaufbau, der selbst schweren Belastungen wie PKW-Verkehr in Einfahrten dauerhaft standhält.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Kostenloses Angebot anfordern
                      </Button>
                    </QuoteModal>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="tel:+4915888640762">
                        <Phone className="w-5 h-5 mr-2" />
                        +49 15888 640762
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Einfahrten & Höfe</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Einfahrten und Hofstellen müssen enorme Drucklasten und Scherkräfte aushalten, besonders beim Lenken von Fahrzeugen. Wir verwenden hierfür spezielle Verbundpflaster oder robustes Natursteinpflaster. Durch das Anlegen eines leichten Gefälles und den Einbau von Entwässerungsrinnen stellen wir sicher, dass Regenwasser schnell und zuverlässig abgeleitet wird und sich im Winter keine gefährlichen Eisflächen bilden.
                    </p>
                  </div>
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Terrassen & Wege</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Die Terrasse ist Ihr Wohnzimmer im Freien. Hier steht die Optik im Vordergrund. Wir verlegen großformatige Terrassenplatten aus Keramik, Betonstein oder edlem Naturstein. Mit filigranen Fugenbildern und exakten Zuschnitten an Rändern und Hauswänden schaffen wir eine makellose Fläche. Auch geschwungene Gartenwege und stilvolle Podeste setzen wir nach Ihren individuellen Vorstellungen um.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Der Weg zum perfekten Pflaster – Unser Prozess</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Jedes erfolgreiche Bauprojekt beginnt mit einer ausführlichen Planung. Wir beraten Sie vor Ort in Augsburg hinsichtlich der optimalen Materialauswahl. Ob rustikales Kopfsteinpflaster, modernes Betonpflaster oder elegante Keramikplatten – wir zeigen Ihnen die Vor- und Nachteile der verschiedenen Beläge auf. Anschließend führen wir die notwendigen Erdarbeiten durch, heben den Boden aus und bringen eine frostsichere Tragschicht aus Schotter ein, die maschinell verdichtet wird.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Darauf folgt das Ausziehen des Splittbetts, in das die Steine schließlich millimetergenau verlegt werden. Durch das finale Abrütteln der Fläche und das Einkehren von speziellem Fugensand entsteht ein fester, in sich verzahnter Belag. Auf Wunsch verwenden wir auch festes Fugenmaterial, das Unkrautwuchs und Ameisenbefall dauerhaft verhindert – für eine pflegeleichte und stets saubere Fläche.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Jetzt beraten lassen
                      </Button>
                    </QuoteModal>
                  </div>
                </div>

                <div className="bg-primary/5 p-8 border-l-4 border-primary mt-8">
                  <h3 className="font-heading text-2xl font-bold mb-3">Sanierung und Reparatur bestehender Flächen</h3>
                  <p className="text-neutral-700 leading-relaxed">
                    Ihre alte Einfahrt hat tiefe Spurrillen oder die Terrassenplatten wackeln? Oft muss nicht die gesamte Fläche neu gemacht werden. Wir bieten auch die professionelle Sanierung bestehender Pflasterflächen an. Wir nehmen die alten Steine auf, erneuern und verdichten den Unterbau und verlegen das Pflaster anschließend neu. So retten wir Ihre Außenanlage kosteneffizient und nachhaltig.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-neutral-950 text-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-3xl font-bold mb-6">Unsere Qualitätsgarantie für Pflasterarbeiten</h2>
                <div className="grid sm:grid-cols-3 gap-8 mt-12 text-left">
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Frostsicherer Unterbau</h3>
                    <p className="text-neutral-400">Durch normgerechte Schotterschichten und Verdichtung verhindern wir Frostschäden und Absackungen.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Perfekte Entwässerung</h3>
                    <p className="text-neutral-400">Exakt geplante Gefälle und Rinnen sorgen für einen schnellen und sicheren Wasserabfluss.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Unkrautfreie Fugen</h3>
                    <p className="text-neutral-400">Auf Wunsch verfugen wir mit festem, wasserdurchlässigem Material gegen Unkraut und Insekten.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      }
    />
  );
}
